import React, { useState, useEffect } from 'react';

// Bumped to v2 after domain migration to devicerisk.compliancehub.wiki — forces returning users to see the updated modal
const STORAGE_KEY = 'drh_welcomed_v2';

export default function WelcomeModal({ onHowItWorks }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setVisible(false);
  };

  const goHowItWorks = () => {
    dismiss();
    onHowItWorks();
  };

  if (!visible) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
      <div className="modal-card p-6 md:p-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                Free Tool — No Account Required
              </span>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                Part of ComplianceHub.wiki
              </span>
            </div>
            <h1 id="welcome-title" className="text-2xl font-bold text-slate-800 leading-tight">
              HIPAA Medical Device<br />Cybersecurity Risk Assessment
            </h1>
          </div>
          <button
            onClick={dismiss}
            className="ml-4 text-slate-400 hover:text-slate-600 text-2xl leading-none flex-shrink-0 mt-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* What it is */}
        <p className="text-slate-600 text-sm mb-5">
          DeviceRisk is a CISO Marketplace micro-tool on <strong className="text-slate-700">ComplianceHub.wiki</strong> (free for members; credits / one-time guest pass otherwise) that helps
          healthcare teams run structured cybersecurity risk assessments for medical devices — aligned with
          HIPAA Security Rule safeguards, STRIDE threat modeling, and the FDA's 2025 cybersecurity guidance.
          Assess any device in minutes and generate a printable compliance report.
        </p>

        {/* 4 differentiators */}
        <div className="grid grid-cols-2 gap-2 mb-5">
          {[
            { icon: '🔒', label: '103 device profiles', sub: 'Pre-filled threat & ePHI data' },
            { icon: '📋', label: 'HIPAA §164 mapped', sub: 'Every question cites its CFR' },
            { icon: '📄', label: 'PDF & CSV export', sub: 'Print-ready compliance report' },
            { icon: '🌐', label: 'Browser-based', sub: 'Runs in your browser; sign in to save reports to your portal' },
          ].map(item => (
            <div key={item.label} className="flex items-start gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
              <span className="text-lg leading-none mt-0.5">{item.icon}</span>
              <div>
                <p className="text-xs font-semibold text-slate-800">{item.label}</p>
                <p className="text-xs text-slate-500">{item.sub}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 gap-2 mb-5">
          {[
            { n: '1', title: 'Select Your Device', desc: 'Choose from 103 profiled medical devices or enter custom device details. Progress saves automatically.' },
            { n: '2', title: 'Assess Safeguards', desc: 'Answer questions on administrative, physical, and technical controls — each mapped to its HIPAA §164 citation.' },
            { n: '3', title: 'Evaluate Threats', desc: 'Rate STRIDE threats by severity and likelihood. Pre-filled profiles for database devices.' },
            { n: '4', title: 'Get Your Risk Score', desc: 'Receive a 0–10 risk score, compliance gap analysis, and export to PDF or CSV — instantly.' },
          ].map(step => (
            <div key={step.n} className="flex items-start gap-3 p-3 rounded-lg bg-blue-50 border border-blue-100">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
                {step.n}
              </div>
              <div>
                <p className="font-semibold text-sm text-slate-800">{step.title}</p>
                <p className="text-xs text-slate-600 mt-0.5">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Regulatory callout */}
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 mb-5 text-xs text-amber-800">
          <strong>Compliance Deadline: November 2026.</strong> The HIPAA Security Rule final rule is expected
          May 2026 — making MFA, AES-256 encryption, quarterly vulnerability scanning, and 24-hour BA breach
          notification <strong>mandatory</strong> with a 180-day compliance window. FDA's revised Feb 2026
          guidance embeds cybersecurity into QMSR/ISO 13485 quality systems for all "cyber devices."
        </div>

        {/* Regulatory foundation / trust signals */}
        <div className="mb-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Built on primary regulatory sources</p>
          <div className="flex flex-wrap gap-1.5">
            {[
              { label: 'HHS OCR', sub: 'HIPAA §164' },
              { label: 'Federal Register', sub: 'NPRM Jan 2025' },
              { label: 'FDA CDRH', sub: 'Section 524B' },
              { label: 'FDA QMSR', sub: 'Feb 2, 2026' },
              { label: 'NIST CSF 2.0', sub: 'Feb 2024' },
              { label: 'HHS 405(d)', sub: 'HICP' },
              { label: 'EU AI Act', sub: 'Aug 2024' },
            ].map(badge => (
              <span key={badge.label} className="inline-flex flex-col items-center px-2.5 py-1.5 rounded-md bg-slate-100 border border-slate-200">
                <span className="text-xs font-semibold text-slate-700 leading-none">{badge.label}</span>
                <span className="text-xs text-slate-400 leading-none mt-0.5">{badge.sub}</span>
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Full source list with citations in{' '}
            <button onClick={goHowItWorks} className="text-blue-500 hover:underline">How It Works → Regulatory Sources</button>.
          </p>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={dismiss}
            className="flex-1 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm transition-colors"
          >
            Start Assessment
          </button>
          <button
            onClick={goHowItWorks}
            className="flex-1 px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg text-sm transition-colors border border-slate-200"
          >
            How It Works
          </button>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          No server — all data stays in your browser. Progress auto-saves so you can return anytime.
        </p>
      </div>
    </div>
  );
}
